/**
 * Derivation des nuits a partir des evenements bruts.
 *
 * Module **pur** : ni React ni Dexie. Il recoit des tableaux et rend des
 * resultats, ce qui permet de le valider sur des scenarios construits — c'est
 * la seule facon de tester un decoupage de nuit sans attendre une nuit.
 *
 * ## Une nuit n'est pas un intervalle
 *
 * Se reveiller a 3 h, consulter son telephone, puis se rendormir est le cas
 * normal. Prendre le dernier usage avant le lever comme heure de coucher ferait
 * tomber une nuit de 7 h a 3 h 30. Une nuit est donc modelisee comme une suite
 * de segments separes par des **interruptions**, chacune arbitree par
 * l'utilisateur au reveil : reveil decompte, ou ignore.
 *
 * ## Ce que ce module ne decide pas
 *
 * Il propose un decoupage par defaut et **conserve tous les candidats**.
 * L'arbitrage final appartient a l'utilisateur, et une nuit `corrigee` n'est
 * jamais recalculee (regle appliquee dans `db/repo.ts`).
 */

import { HEURE_MS, MINUTE_MS, dayKeyOf, epoch, heureLocale } from '../domain/time'
import type {
  Anomalie,
  Candidat,
  DayKey,
  Evenement,
  Instant,
  Interruption,
  Nuit,
  Reglages,
  TypeEvenement,
} from '../domain/types'

// ---------------------------------------------------------------------------
// Bornes de plausibilite
//
// Elles ne sont pas reglables : ce ne sont pas des preferences mais des
// garde-fous contre un appariement absurde (une sieste prise pour une nuit, un
// week-end entier pris pour une seule nuit). Une amplitude hors de ces bornes
// produit une anomalie affichee, jamais une nuit silencieusement fausse.
// ---------------------------------------------------------------------------

export const AMPLITUDE_MIN_MS = 3 * HEURE_MS
export const AMPLITUDE_MAX_MS = 14 * HEURE_MS

/** Fenetre de recherche du coucher en amont du lever. */
const RECHERCHE_MAX_MS = 16 * HEURE_MS

/** Plage horaire locale ou un evenement peut marquer un coucher. */
const COUCHER_DEBUT_H = 19
const COUCHER_FIN_H = 5

/** Plage horaire locale ou un evenement peut marquer un lever. */
const LEVER_DEBUT_H = 4
const LEVER_FIN_H = 12

const TYPES_COUCHER: TypeEvenement[] = ['fermeture', 'branche']
const TYPES_LEVER: TypeEvenement[] = ['alarme', 'debranche']

function dansPlageCoucher(at: Instant): boolean {
  const h = heureLocale(at)
  return h >= COUCHER_DEBUT_H || h < COUCHER_FIN_H
}

function dansPlageLever(at: Instant): boolean {
  const h = heureLocale(at)
  return h >= LEVER_DEBUT_H && h < LEVER_FIN_H
}

// ---------------------------------------------------------------------------
// Mesures derivees
//
// Amplitude, temps pose, reveils et efficacite sont **calcules a la demande**,
// jamais stockes. Les figer en base voudrait dire qu'une bascule d'arbitrage
// laisse derriere elle des chiffres perimes, et il suffirait d'un oubli pour
// afficher un total qui ne correspond plus a ses propres segments.
// ---------------------------------------------------------------------------

export interface MesuresNuit {
  amplitudeMs: number
  tempsPoseMs: number
  reveils: number
  /** Part de l'amplitude reellement passee telephone pose, entre 0 et 1. */
  efficacite: number
}

export function mesurerNuit(nuit: Nuit): MesuresNuit {
  const amplitudeMs = Math.max(0, epoch(nuit.fin) - epoch(nuit.debut))
  const decomptees = nuit.interruptions.filter((i) => !i.ignoree)
  const perdu = decomptees.reduce(
    (somme, i) => somme + Math.max(0, epoch(i.fin) - epoch(i.debut)),
    0,
  )
  const tempsPoseMs = Math.max(0, amplitudeMs - perdu)
  return {
    amplitudeMs,
    tempsPoseMs,
    reveils: decomptees.length,
    efficacite: amplitudeMs > 0 ? tempsPoseMs / amplitudeMs : 0,
  }
}

// ---------------------------------------------------------------------------
// Derivation
// ---------------------------------------------------------------------------

export interface ResultatDerivation {
  nuits: Nuit[]
  anomalies: Anomalie[]
}

export function deriverNuits(
  evenements: Evenement[],
  reglages: Reglages,
  maintenant: Instant = new Date().toISOString(),
): ResultatDerivation {
  const tries = [...evenements].sort((a, b) => a.at.localeCompare(b.at))
  const nuits: Nuit[] = []
  const anomalies: Anomalie[] = []
  const datesTraitees = new Set<DayKey>()

  for (const [date, candidatsFin] of grouperLevers(tries).entries()) {
    const fin = choisirFin(candidatsFin, reglages.preferenceFin)
    if (!fin) continue
    datesTraitees.add(date)

    const nuit = construireNuit(tries, fin, candidatsFin, date, reglages)
    if ('anomalie' in nuit) anomalies.push(nuit.anomalie)
    else nuits.push(nuit.nuit)
  }

  // Repli : les matins sans aucun signal de raccourci sont rattrapes par les
  // ouvertures de l'app elle-meme. Moins fidele — l'app n'est pas ouverte au
  // moment exact du coucher — donc toujours etiquete comme estime.
  for (const nuit of deriverParRepli(tries, datesTraitees, maintenant)) {
    nuits.push(nuit)
  }

  nuits.sort((a, b) => a.date.localeCompare(b.date))
  anomalies.sort((a, b) => a.date.localeCompare(b.date))
  return { nuits, anomalies }
}

/** Candidats de lever, regroupes par matin. */
function grouperLevers(tries: Evenement[]): Map<DayKey, Evenement[]> {
  const parJour = new Map<DayKey, Evenement[]>()
  for (const e of tries) {
    if (!TYPES_LEVER.includes(e.type)) continue
    if (!dansPlageLever(e.at)) continue
    const cle = dayKeyOf(e.at)
    const liste = parJour.get(cle)
    if (liste) liste.push(e)
    else parJour.set(cle, [e])
  }
  return parJour
}

/**
 * Le lever retenu.
 *
 * Par defaut le **premier** signal du matin : c'est l'instant ou le telephone
 * est repris, donc le plus proche du reveil reel. Debrancher peut arriver bien
 * plus tard (apres la douche), arreter l'alarme jamais.
 */
function choisirFin(candidats: Evenement[], preference: Reglages['preferenceFin']): Evenement | null {
  if (candidats.length === 0) return null
  if (preference !== 'premier') {
    const prefere = candidats.find((e) => e.type === preference)
    if (prefere) return prefere
  }
  return candidats[0] ?? null
}

type Issue = { nuit: Nuit } | { anomalie: Anomalie }

function construireNuit(
  tries: Evenement[],
  fin: Evenement,
  candidatsFin: Evenement[],
  date: DayKey,
  reglages: Reglages,
): Issue {
  const finMs = epoch(fin.at)
  const planchier = finMs - RECHERCHE_MAX_MS

  const fenetre = tries.filter((e) => {
    const t = epoch(e.at)
    return t >= planchier && t < finMs
  })

  const candidatsDebut = fenetre.filter(
    (e) => TYPES_COUCHER.includes(e.type) && dansPlageCoucher(e.at),
  )

  if (candidatsDebut.length === 0) {
    return {
      anomalie: {
        date,
        raison: 'debut-manquant',
        message:
          'Aucun signal de coucher dans les 16 h precedant le lever. Le telephone n’a peut-etre pas ete branche, ou les automatisations d’app ne couvrent pas les apps utilisees ce soir-la.',
        evenements: fenetre,
      },
    }
  }

  const debut = choisirDebut(candidatsDebut, fenetre, fin, reglages)
  const amplitude = finMs - epoch(debut.at)

  if (amplitude < AMPLITUDE_MIN_MS || amplitude > AMPLITUDE_MAX_MS) {
    return {
      anomalie: {
        date,
        raison: 'duree-hors-bornes',
        message: `Amplitude de ${Math.round(amplitude / HEURE_MS)} h, hors des bornes plausibles (3 h a 14 h). Les evenements bruts sont conserves : la nuit peut etre saisie a la main.`,
        evenements: fenetre,
      },
    }
  }

  return {
    nuit: {
      date,
      debut: debut.at,
      fin: fin.at,
      interruptions: decouperInterruptions(tries, debut.at, fin.at, reglages),
      candidatsDebut: candidatsDebut.map(versCandidat),
      candidatsFin: candidatsFin.map(versCandidat),
      origineDebut: debut.type,
      origineFin: fin.type,
      source: 'raccourci',
      corrigee: false,
      modifieLe: fin.creeLe,
    },
  }
}

function versCandidat(e: Evenement): Candidat {
  return { at: e.at, type: e.type }
}

/**
 * Le coucher retenu : **le premier candidat suivi d'un long silence**.
 *
 * ## Pourquoi cette regle et pas « le dernier candidat »
 *
 * Le dernier candidat avant le lever est presque toujours un reveil nocturne,
 * pas un coucher. C'est le defaut que cette regle existe pour eviter.
 *
 * ## Pourquoi pas « le premier candidat de la soiree »
 *
 * Une soiree produit des dizaines de fermetures d'app. La premiere marque le
 * debut de la soiree, pas le coucher. Ce qu'on cherche est la bascule : le
 * moment apres lequel le telephone se tait durablement.
 *
 * On parcourt donc les candidats du plus tot au plus tard et on retient le
 * premier dont le silence qui suit — jusqu'au prochain evenement quel qu'il
 * soit, ou jusqu'au lever — atteint `seuilNuit`.
 *
 * ## Sa limite, assumee
 *
 * Un coup d'oeil au telephone moins de `seuilNuit` apres l'avoir pose fait
 * glisser le coucher jusqu'a lui. Avec 90 min par defaut, une verification a
 * 01 h 10 apres un coucher a 23 h 55 sera prise pour la fin de soiree. C'est
 * indecidable a partir des seuls evenements — 70 min de silence peut etre du
 * sommeil comme une lecture. D'ou `candidatsDebut`, qui conserve tout : la page
 * Aujourd'hui laisse retenir 23 h 55 d'un geste.
 *
 * Si aucun candidat n'est suivi d'un silence suffisant (usage continu toute la
 * nuit), on retombe sur le dernier candidat et les garde-fous d'amplitude font
 * leur office.
 */
function choisirDebut(
  candidats: Evenement[],
  fenetre: Evenement[],
  fin: Evenement,
  reglages: Reglages,
): Evenement {
  const seuil = reglages.seuilNuitMinutes * MINUTE_MS
  const finMs = epoch(fin.at)

  for (const c of candidats) {
    const tc = epoch(c.at)
    const suivant = fenetre.find((e) => epoch(e.at) > tc)
    const silence = (suivant ? epoch(suivant.at) : finMs) - tc
    if (silence >= seuil) return c
  }

  return candidats[candidats.length - 1] as Evenement
}

/**
 * Decoupe les usages du telephone survenus entre le coucher et le lever.
 *
 * ## Deux etapes, et pourquoi pas une seule
 *
 * On appaire d'abord chaque ouverture avec sa fermeture, **puis** on fusionne
 * les usages proches. L'ordre importe : regrouper directement les evenements
 * par leur ecart couperait en deux une consultation continue de 40 minutes, ou
 * l'ouverture et la fermeture sont par construction eloignees. Une paire
 * ouverture/fermeture est un seul usage quelle que soit sa duree — c'est le
 * silence *entre* deux usages qui decide s'ils se rejoignent.
 *
 * Le regroupement evite ensuite qu'ouvrir Messages, le fermer puis ouvrir
 * Safari ne compte trois reveils la ou il n'y en a eu qu'un.
 */
function decouperInterruptions(
  tries: Evenement[],
  debut: Instant,
  fin: Instant,
  reglages: Reglages,
): Interruption[] {
  const t0 = epoch(debut)
  const t1 = epoch(fin)
  const dedans = tries.filter((e) => {
    if (e.type !== 'ouverture' && e.type !== 'fermeture') return false
    const t = epoch(e.at)
    return t > t0 && t < t1
  })

  // Etape 1 : appariement.
  const usages: { debut: Instant; fin: Instant }[] = []
  let ouverte: Evenement | null = null
  for (const e of dedans) {
    if (e.type === 'ouverture') {
      // Deux ouvertures sans fermeture entre elles : l'usage a commence a la
      // premiere et continue, on ne le redemarre pas.
      if (!ouverte) ouverte = e
      continue
    }
    if (ouverte) {
      usages.push({ debut: ouverte.at, fin: e.at })
      ouverte = null
    } else {
      // Fermeture orpheline : on sait jusqu'a quand le telephone a servi,
      // jamais depuis quand. Duree nulle, signalee comme telle plus bas.
      usages.push({ debut: e.at, fin: e.at })
    }
  }
  // Ouverture jamais refermee avant le lever : meme raisonnement en miroir.
  if (ouverte) usages.push({ debut: ouverte.at, fin: ouverte.at })

  // Etape 2 : fusion des usages separes par un silence trop court.
  const fusion = reglages.seuilFusionMinutes * MINUTE_MS
  const grappes: { debut: Instant; fin: Instant }[] = []
  for (const u of usages) {
    const courante = grappes[grappes.length - 1]
    if (courante && epoch(u.debut) - epoch(courante.fin) <= fusion) {
      courante.fin = u.fin
    } else {
      grappes.push({ ...u })
    }
  }

  const seuilReveil = reglages.seuilReveilMinutes * MINUTE_MS
  return grappes.map((g) => {
    const duree = epoch(g.fin) - epoch(g.debut)
    // Une duree nulle n'est pas un usage bref, c'est un usage non mesure. On
    // ne l'ignore donc jamais d'office : le seuil de reveil le ferait
    // disparaitre alors que le reveil, lui, a bien eu lieu.
    const dureeInconnue = duree === 0
    return {
      debut: g.debut,
      fin: g.fin,
      ignoree: !dureeInconnue && duree < seuilReveil,
      dureeInconnue,
    }
  })
}

/**
 * Repli sur les ouvertures de la PWA quand aucun raccourci n'a repondu.
 *
 * Nettement moins fidele : l'app n'est pas ouverte au moment ou le telephone
 * est pose. Le resultat est donc toujours marque `heuristique` et affiche comme
 * une estimation. Aucune interruption n'est deduite — il n'y a pas de quoi.
 */
function deriverParRepli(
  tries: Evenement[],
  datesTraitees: Set<DayKey>,
  maintenant: Instant,
): Nuit[] {
  const matins = new Map<DayKey, Evenement>()
  for (const e of tries) {
    if (e.type !== 'appli' || !dansPlageLever(e.at)) continue
    const cle = dayKeyOf(e.at)
    if (!datesTraitees.has(cle) && !matins.has(cle)) matins.set(cle, e)
  }

  const nuits: Nuit[] = []
  for (const [date, fin] of matins.entries()) {
    const finMs = epoch(fin.at)
    const veille = tries.filter((e) => {
      if (e.type !== 'appli') return false
      const t = epoch(e.at)
      return t >= finMs - RECHERCHE_MAX_MS && t < finMs && dansPlageCoucher(e.at)
    })
    const debut = veille[veille.length - 1]
    if (!debut) continue

    const amplitude = finMs - epoch(debut.at)
    if (amplitude < AMPLITUDE_MIN_MS || amplitude > AMPLITUDE_MAX_MS) continue

    nuits.push({
      date,
      debut: debut.at,
      fin: fin.at,
      interruptions: [],
      candidatsDebut: veille.map(versCandidat),
      candidatsFin: [versCandidat(fin)],
      origineDebut: 'appli',
      origineFin: 'appli',
      source: 'heuristique',
      corrigee: false,
      modifieLe: maintenant,
    })
  }

  return nuits
}
